import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import styles from "./CancelProrationModal.module.css";

/**
 * Props:
 *  - accessToken: string (required)
 *  - onClose(): void
 *  - onDone?(): void   // optional: parent can refresh after portal returns
 */
export default function CancelProrationModal({ accessToken, onClose, onDone }) {
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // ---- helpers ----
  const asEpoch = (x) => {
    if (x == null) return null;
    if (typeof x === "number") return x;
    const t = Date.parse(x);
    return Number.isNaN(t) ? null : Math.floor(t / 1000);
  };
  const fmtDate = (x) => {
    const sec = asEpoch(x);
    const d = sec ? new Date(sec * 1000) : new Date(x);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };
  const fmtMoneyCents = (cents = 0, currency = "USD") =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format((cents || 0) / 100);

  // ---- fetch preview on mount (from backend) ----
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        setError(null);
        setLoading(true);
        const auth = accessToken.startsWith("Bearer ")
          ? accessToken
          : `Bearer ${accessToken}`;

        // Your backend must implement this (we wrote it earlier):
        // returns: {currency, preview_credit_cents, period_start, period_end, is_trial, proration_lines?}
        const { data } = await axios.post(
          `${import.meta.env.VITE_BACKEND_URL}/payment/cancel-preview`,
          {},
          { headers: { Authorization: auth } }
        );

        if (!live) return;

        const start = data.period_start;
        const end = data.period_end;
        const now = Math.floor(Date.now() / 1000);
        const totalDays = Math.max(1, Math.ceil((end - start) / 86400));
        const remainingDays = Math.max(
          0,
          Math.ceil((end - Math.min(end, now)) / 86400)
        );

        setInvoice({
          currency: (data.currency || "usd").toUpperCase(),
          isTrial: !!data.is_trial,
          startDateSec: start,
          endDateSec: end,
          totalBillingDays: totalDays,
          remainingDays,
          estimatedRefundCents: data.is_trial
            ? 0
            : data.preview_credit_cents || 0,
          summaryLabel: data.is_trial
            ? "You’re still in a trial period. Cancelling now ends access immediately. No refund is due."
            : "If you cancel in Stripe, we’ll refund the unused portion of this billing period.",
          proration_lines: data.proration_lines || [],
        });
      } catch (e) {
        if (!live) return;
        setError(
          e?.response?.data?.error || e.message || "Failed to load estimate"
        );
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [accessToken]);

  // ---- proceed: open Billing Portal ----
  const proceed = async () => {
    try {
      setBusy(true);
      const auth = accessToken.startsWith("Bearer ")
        ? accessToken
        : `Bearer ${accessToken}`;
      const { data } = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/payment/billing-portal-session`,
        {},
        { headers: { Authorization: auth } }
      );
      setBusy(false);
      onClose?.();
      onDone?.();
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      setBusy(false);
      setError(
        e?.response?.data?.error || e.message || "Could not open billing portal"
      );
    }
  };

  // ---- render ----
  return (
    <div className={styles.overlay}>
      <div
        className={styles.modal}
        aria-busy={loading || busy ? "true" : "false"}
      >
        <h2>We’re sad to see you go 😢</h2>

        {error && <div className={styles.error}>{error}</div>}

        {!invoice && !error && (
          <p className={styles.summaryNote}>
            Calculating your refund estimate…
          </p>
        )}

        {invoice && (
          <>
            {/* Optional: Trial badge */}
            {invoice.isTrial && (
              <div className={styles.trialBadge}>Trial Active</div>
            )}

            {/* Billing Period */}
            <div className={styles.lineItem}>
              <span>Billing Period</span>
              <span>
                {fmtDate(invoice.startDateSec)} – {fmtDate(invoice.endDateSec)}
              </span>
            </div>

            {/* Trial Info */}
            {invoice.isTrial && (
              <div className={styles.lineItem}>
                <span>Trial Period</span>
                <span>Ends {fmtDate(invoice.endDateSec)}</span>
              </div>
            )}

            {/* Remaining Days */}
            <div className={styles.lineItem}>
              <span>Remaining Days</span>
              <span>
                {invoice.remainingDays} / {invoice.totalBillingDays}
              </span>
            </div>

            {/* Refund */}
            {!invoice.isTrial && (
              <div className={styles.lineItem}>
                <span>Estimated Refund</span>
                <span className={styles.credit}>
                  -
                  {fmtMoneyCents(
                    invoice.estimatedRefundCents,
                    invoice.currency
                  )}
                </span>
              </div>
            )}

            {/* Optional proration breakdown */}
            {!invoice.isTrial && invoice.proration_lines?.length > 0 && (
              <div className={styles.breakdown}>
                {invoice.proration_lines.map((li, i) => (
                  <div key={i} className={styles.breakdownLine}>
                    <span>{li.description || "Proration"}</span>
                    <span>{fmtMoneyCents(li.amount, invoice.currency)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Summary */}
            <p className={styles.summaryNote}>{invoice.summaryLabel}</p>
          </>
        )}

        <div className={styles.actions}>
          <button
            className={styles.cancelBtn}
            onClick={onClose}
            disabled={busy}
          >
            Never mind
          </button>
          <button
            className={styles.payBtn}
            onClick={proceed}
            disabled={busy || !!error || !invoice}
          >
            {busy ? "Processing…" : "Proceed to Cancel"}
          </button>
        </div>
      </div>
    </div>
  );
}
